import { useEffect, useMemo, useState } from "react";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";

import { Badge, Popover } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useRedemptionNotification, useSeen } from "../api/redemption";

// import { useSendResetPasswordLink } from '../api/auth';

export type Voucher = {
  id: number;
  messageId: number;
  title: string;
  isSeen: number;
  description: string;
  createdAt: string;
};

function Header() {
  const { logout: signOut, user } = useAuth() as AuthContextValue;
  const location = useLocation().pathname.substring(1);
  const index = location.indexOf("/");
  const title = index >= 0 ? location.substring(0, index) : location;

  const [voucherList, setVoucherList] = useState<Voucher[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { data: vouchers, isLoading, refetch } = useRedemptionNotification();

  const { mutateAsync: seen } = useSeen();

  // const { data: seen, isLoadingSeen, refetchSeen } = useSeen();

  // const { mutateAsync: sendLink } = useSendResetPasswordLink();

  const navigate = useNavigate();

  const handleSeeMoreClick = () => {
    setIsPopoverOpen(false);
    if (user?.roleName.startsWith("ROLE_MPC")) {
      // navigate("/payment?status=payed");
      navigate("/redemptions");
    }
    if (user?.roleName.startsWith("ROLE_MFI")) {
      navigate("/voucher?status=redeemed");
    }
  };

  useEffect(() => {
    if (vouchers?.data?.data) {
      const transformedVouchers = vouchers.data.data.map(
        (item: {
          id: number;
          messageId: number;
          title: string;
          seen: boolean;
          description: string;
          createdAt: string;
        }) => ({
          id: item.id,
          messageId: item.messageId,
          title: item.title,
          isSeen: item.seen ? 1 : 0,
          description: item.description,
          createdAt: item.createdAt,
        })
      );
      setVoucherList(transformedVouchers);
    }
  }, [vouchers]);

  // Filter vouchers by status (unread - isSeen === 0)
  const vouchersWithStatusRedeemed = useMemo(
    () => voucherList.filter((voucher) => voucher.isSeen === 0),
    [voucherList]
  );

  // Sort vouchers by createdAt (handling the date format correctly)
  const sortedVouchers = useMemo(
    () =>
      vouchersWithStatusRedeemed.sort((a, b) => {
        const dateA = new Date(a.createdAt.split("/").reverse().join("-")); // Convert to yyyy-MM-dd
        const dateB = new Date(b.createdAt.split("/").reverse().join("-"));
        return dateB.getTime() - dateA.getTime();
      }),
    [vouchersWithStatusRedeemed]
  );

  // Select the latest 5 vouchers
  const latestVouchers = useMemo(
    () => sortedVouchers.slice(0, 5),
    [sortedVouchers]
  );

  // Generate notifications dynamically based on voucherList data
  const notifications = useMemo(
    () =>
      latestVouchers.map((voucher) => ({
        id: voucher.id,
        title: voucher.title,
        description: voucher.description,
        isSeen: voucher.isSeen,
        messageId: voucher.messageId,
      })),
    [latestVouchers]
  );

  const notificationContent = (
    <div className="max-w-xs rounded-lg shadow-lg shadow-gray-200 cursor-pointer ">
      {!isPopoverOpen && notifications.map((notification) => (
        <div
          key={notification.id}
          className="py-3 border-b last:border-b-2 rounded-lg p-3 border-b-gray-200 
           bg-white text-gray-800
           flex items-center justify-between"
          onClick={() => {
            try {
              setIsPopoverOpen(false);
              seen({ id: notification.messageId });
              if (user?.roleName.startsWith("ROLE_MFI")) {
                // navigate("/payment?status=payied");
                navigate(`/voucher/${notification.id}`);
              }
              if (user?.roleName.startsWith("ROLE_MPC")) {
                navigate("/redemptions");
              }
            } catch (err: any) {
              // console.log("error", err);
            }
          }}
        >
          <div onClick={() => navigate(`/voucher/${notification.id}`)}>
            <strong className="block font-semibold text-sm">
              {notification.title}
            </strong>
            <p className="mt-1 text-xs">{notification.description}</p>
          </div>
        </div>
      ))}

      <div
        className="text-gray-600 text-center py-2 cursor-pointer"
        onClick={handleSeeMoreClick}
      >
        {vouchersWithStatusRedeemed.length == 0 ? (
          <>No New Notification.</>
        ) : (
          <div
            className="text-gray-600 text-center py-2 cursor-pointer"
            onClick={() => navigate("/voucher/")}
          >
            see more
          </div>
        )}
      </div>
    </div>
  );

  const sendResetPasswordLink = async () => {
    // sendLink('')
    // if (profile && profile.email) {
    //     const result = await sendLink(profile.email);
    //     if (!result.error) {
    //         message.success('Message reset link has been sent to your email.')
    //     }
    // } else {
    //     message.error('Please login first');
    // }
  };

  const content = (
    <div style={{ color: "black", width: 200 }}>
      <div
        onClick={() => navigate("/profile")}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
      >
        <svg
          width="18"
          height="15"
          viewBox="0 0 18 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.0703 10.9766L17.7812 11.3867C17.8633 11.4141 17.8906 11.5234 17.8633 11.6055C17.6719 12.207 17.3711 12.7266 16.9609 13.1914C16.9062 13.2461 16.7969 13.2734 16.7148 13.2188L16.0039 12.8086C15.7031 13.0547 15.375 13.2734 14.9922 13.3828V14.2031C14.9922 14.3125 14.9375 14.3945 14.8555 14.3945C14.2539 14.5312 13.6523 14.5312 13.0234 14.3945C12.9414 14.3945 12.8867 14.3125 12.8867 14.2031V13.3828C12.5039 13.2734 12.1758 13.0547 11.875 12.8086L11.1641 13.2188C11.082 13.2734 10.9727 13.2461 10.918 13.1914C10.5078 12.7266 10.1797 12.207 10.0156 11.6055C9.98828 11.5234 10.0156 11.4141 10.0977 11.3867L10.8086 10.9766C10.7266 10.5938 10.7266 10.1836 10.8086 9.80078L10.0977 9.39062C10.0156 9.36328 9.98828 9.25391 10.0156 9.17188C10.1797 8.57031 10.5078 8.05078 10.918 7.58594C10.9727 7.53125 11.082 7.50391 11.1641 7.55859L11.875 7.96875C12.1758 7.72266 12.5039 7.50391 12.8867 7.39453V6.57422C12.8867 6.46484 12.9414 6.38281 13.0234 6.38281C13.625 6.24609 14.2266 6.24609 14.8555 6.38281C14.9375 6.38281 14.9922 6.46484 14.9922 6.57422V7.39453C15.375 7.50391 15.7031 7.72266 16.0039 7.96875L16.7148 7.55859C16.7969 7.50391 16.9062 7.53125 16.9609 7.58594C17.3711 8.05078 17.6719 8.57031 17.8633 9.17188C17.8906 9.25391 17.8633 9.36328 17.7812 9.39062L17.0703 9.80078C17.1523 10.1836 17.1523 10.5938 17.0703 10.9766ZM13.9531 11.7148C14.6641 11.7148 15.2656 11.1133 15.2656 10.375C15.2656 9.66406 14.6641 9.0625 13.9531 9.0625C13.2148 9.0625 12.6133 9.66406 12.6133 10.375C12.6133 11.1133 13.2148 11.7148 13.9531 11.7148ZM6.51562 7.75C4.57422 7.75 3.01562 6.19141 3.01562 4.25C3.01562 2.33594 4.57422 0.75 6.51562 0.75C8.42969 0.75 10.0156 2.33594 10.0156 4.25C10.0156 6.19141 8.42969 7.75 6.51562 7.75ZM12.0117 13.957V14.2031C12.0117 14.3125 12.0391 14.4219 12.0664 14.5312C11.8477 14.668 11.6016 14.75 11.3281 14.75H1.70312C0.964844 14.75 0.390625 14.1758 0.390625 13.4375V12.3164C0.390625 10.293 2.03125 8.625 4.05469 8.625H4.51953C5.12109 8.92578 5.80469 9.0625 6.51562 9.0625C7.22656 9.0625 7.88281 8.92578 8.48438 8.625H8.94922C9.05859 8.625 9.16797 8.65234 9.27734 8.67969C9.25 8.76172 9.19531 8.81641 9.16797 8.89844C9.03125 9.39062 9.22266 9.91016 9.66016 10.1562L9.87891 10.293C9.87891 10.3477 9.87891 10.4297 9.87891 10.4844L9.66016 10.6211C9.22266 10.8672 9.03125 11.3867 9.16797 11.8789C9.38672 12.5625 9.76953 13.2461 10.2891 13.7656C10.4805 13.9844 10.7812 14.1211 11.0547 14.1211C11.2461 14.1211 11.4375 14.0664 11.6016 13.9844L11.8203 13.8477C11.875 13.875 11.9297 13.9297 12.0117 13.957Z"
            fill="#EE8F00"
          />
        </svg>

        <div>My profile</div>
      </div>

      <div
        onClick={() => navigate("/changePassword")}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.3906 5.58984C14.3906 8.24219 12.2305 10.375 9.57812 10.375C9.25 10.375 8.94922 10.3477 8.67578 10.293L8.01953 11.0312C7.88281 11.168 7.71875 11.25 7.52734 11.25H6.51562V12.3438C6.51562 12.7266 6.21484 13 5.85938 13H4.76562V14.0938C4.76562 14.4766 4.46484 14.75 4.10938 14.75H1.04688C0.664062 14.75 0.390625 14.4766 0.390625 14.0938V11.9609C0.390625 11.7969 0.445312 11.6328 0.582031 11.4961L4.98438 7.09375C4.84766 6.60156 4.76562 6.10938 4.76562 5.5625C4.76562 2.91016 6.89844 0.777344 9.55078 0.75C12.2305 0.75 14.3906 2.91016 14.3906 5.58984ZM9.57812 4.25C9.57812 4.98828 10.1523 5.5625 10.8906 5.5625C11.6016 5.5625 12.2031 4.98828 12.2031 4.25C12.2031 3.53906 11.6016 2.9375 10.8906 2.9375C10.1523 2.9375 9.57812 3.53906 9.57812 4.25Z"
            fill="#EE8F00"
          />
        </svg>

        <div>Change password</div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
        onClick={() => {
          const token = localStorage.getItem("token");
          if (token) {
            signOut();
            localStorage.removeItem("token");
            navigate("/");
          }
        }}
      >
        <svg
          width="15"
          height="12"
          viewBox="0 0 15 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.9805 6.21484L9.38672 10.8086C8.97656 11.2188 8.26562 10.9453 8.26562 10.3438V7.71875H4.54688C4.16406 7.71875 3.89062 7.44531 3.89062 7.0625V4.4375C3.89062 4.08203 4.16406 3.78125 4.54688 3.78125H8.26562V1.15625C8.26562 0.582031 8.94922 0.28125 9.38672 0.691406L13.9805 5.28516C14.2266 5.55859 14.2266 5.96875 13.9805 6.21484ZM5.64062 10.6719C5.64062 10.8633 5.47656 11 5.3125 11H3.01562C1.56641 11 0.390625 9.82422 0.390625 8.375V3.125C0.390625 1.67578 1.56641 0.5 3.01562 0.5H5.3125C5.47656 0.5 5.64062 0.664062 5.64062 0.828125V1.92188C5.64062 2.11328 5.47656 2.25 5.3125 2.25H3.01562C2.52344 2.25 2.14062 2.66016 2.14062 3.125V8.375C2.14062 8.86719 2.52344 9.25 3.01562 9.25H5.3125C5.47656 9.25 5.64062 9.41406 5.64062 9.57812V10.6719Z"
            fill="#EE8F00"
          />
        </svg>

        <div>Logout</div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="print:hidden flex"
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          height: 80,
          backgroundColor: "#0D5D54",
          borderRadius: 8.5,
          paddingLeft: 47,
          paddingRight: 47,
          paddingTop: 26,
          paddingBottom: 26,
          position: "sticky", 
          top: 0, 
          zIndex: 1000, 
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 24,
            color: "white",
            textTransform: "uppercase",
          }}
        >
          {title === "redemptions"
            ? "payment"
            : title.indexOf("import") >= 0
            ? "Import"
            : title}
        </div>
        {user && (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Popover
              destroyTooltipOnHide
              placement="bottomRight"
              content={notificationContent}
              trigger="click"
              className="cursor-pointer "
            >
              <Badge
                count={
                  <div className="flex justify-center  items-center text-xs w-[24px] h-[24px] rounded-full bg-green-200 ">
                    {vouchersWithStatusRedeemed.length > 9 ? (
                      <span>
                        9<sup>+</sup>
                      </span>
                    ) : (
                      vouchersWithStatusRedeemed.length
                    )}
                  </div>
                }
                offset={[0, 2]}
                overflowCount={9}
                // size="default"
                // style={{ color: "red", fontSize: 12, width: 8 }}
                // color={`${notifications.length > 0 ? "green" : "gray"}`}
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="cursor-pointer text-white"
                >
                  <path
                    d="M23.75 21.25V14.75C23.125 14.875 22.5 15 21.875 15H21.25V22.5H8.75V13.75C8.75 10.25 11.5 7.5 15 7.5C15.125 5.875 15.875 4.5 16.875 3.375C16.5 2.875 15.75 2.5 15 2.5C13.625 2.5 12.5 3.625 12.5 5V5.375C8.75 6.5 6.25 9.875 6.25 13.75V21.25L3.75 23.75V25H26.25V23.75L23.75 21.25ZM12.5 26.25C12.5 27.625 13.625 28.75 15 28.75C16.375 28.75 17.5 27.625 17.5 26.25H12.5Z"
                    fill="white"
                  />
                </svg>
              </Badge>
            </Popover>

            {/* <circle cx="21.8478" cy="8.15302" r="4.8913" fill="#CCCCCC" /> */}
            {/* <circle cx="21.8478" cy="8.15302" r="4.8913" fill="#17BB2F" /> */}

            {/* {user && user.avatar_url && <Image src={profile?.avatar_url} style={{ width: 50, height: 50, borderRadius: 50 }} />} */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: 5 }}
              className="px-2"
            >
              <div style={{ fontWeight: 500, fontSize: 17.63, color: "white" }}>
                {user.fullName}
              </div>
              <div
                style={{ fontWeight: 400, fontSize: 11.88, color: "#9B9B9B" }}
              >
                @{user.userName}
              </div>
            </div>
            <Popover
              placement="bottomLeft"
              title={<div style={{ color: "black" }}>{user.fullName}</div>}
              content={content}
            >
              <svg
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.79063 6.24004L0 1.56001L1.59763 0L4.79063 3.12002L7.98364 0L9.58127 1.56001L4.79063 6.24115V6.24004Z"
                  fill="white"
                />
              </svg>
            </Popover>
          </div>
        )}
      </div>
      <div className="print:block uppercase text-2xl underline hidden">
        {title}
      </div>
    </>
  );
}

export default Header;
